import React from "react";
import {
    Box, Heading, Text, VStack, HStack, Badge, Icon, Flex, Table,
    Thead, Tbody, Tr, Th, Td, SimpleGrid
} from "@chakra-ui/react";
import { FiDollarSign } from "react-icons/fi";
import { formatCurrency, formatInvoiceDate, getDueInBadge, isSupersededInvoice } from "../utils/formatHelpers";
import { PageHeader, PdfActionIcons, getPdfViewUrl, getInvoiceStatusBadgeProps } from "./portalUi";
import { COLORS } from "../styles/colors";
import moment from "moment";

const getInvoiceTotal = (invoice) => {
    const subtotal = invoice.invoice_line_items?.length > 0
        ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
    const gst = invoice.gst_amount || 0;
    return subtotal + gst;
};

const isPaidInvoice = (invoice) => {
    const s = (invoice.status || "").toLowerCase();
    return s === "paid";
};

const isInstalmentInvoice = (invoice) => {
    const descriptions = (invoice.invoice_line_items || [])
        .map((item) => item.description || "")
        .join(" ")
        .toLowerCase();
    const type = (invoice.invoice_type || "").toLowerCase();
    return descriptions.includes("instalment") || descriptions.includes("installment")
        || type.includes("instalment") || type.includes("installment");
};

const getInvoiceDownloadFilename = (invoice) => {
    const label = invoice.invoice_line_items?.[0]?.description
        || invoice.invoice_type
        || invoice.invoice_number
        || invoice.id;
    return `${String(label).replace(/\s+/g, "_")}.pdf`;
};

const hasInvoicePdf = (invoice) => !!(getPdfViewUrl(invoice) || invoice.pdf_file_id || invoice.pdf_url);

const isOverdueUnpaid = (invoice) => {
    if (isPaidInvoice(invoice) || !invoice.due_date) return false;
    const due = moment(invoice.due_date).startOf("day");
    if (!due.isValid()) return false;
    return due.isBefore(moment().startOf("day"));
};

export const InvoicesTab = ({ certifications, invoices, handleViewInvoice, isMobile }) => {
    const allInvoices = Object.values(invoices).flat().filter((inv) => !isSupersededInvoice(inv));

    const totalCourseFee = allInvoices.reduce((sum, inv) => sum + getInvoiceTotal(inv), 0);
    const amountPaid = allInvoices
        .filter(isPaidInvoice)
        .reduce((sum, inv) => sum + getInvoiceTotal(inv), 0);
    const totalBalance = allInvoices
        .filter((inv) => !isPaidInvoice(inv))
        .reduce((sum, inv) => sum + getInvoiceTotal(inv), 0);
    const overdueAmount = allInvoices
        .filter(isOverdueUnpaid)
        .reduce((sum, inv) => sum + getInvoiceTotal(inv), 0);

    const instalmentInvoices = allInvoices.filter(isInstalmentInvoice);
    const paidInstalments = instalmentInvoices.filter(isPaidInvoice).length;
    const totalInstalments = instalmentInvoices.length;

    return (
        <VStack spacing={{ base: 4, md: 5 }} align="stretch">
            <PageHeader
                icon={FiDollarSign}
                iconColor="green.500"
                title="Invoices"
                subtitle="Billing statements and payment status"
            />

            {allInvoices.length > 0 && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 2.5, md: 4 }}>
                    <Box
                        p={{ base: 3, md: 4 }}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderLeftWidth="3px"
                        borderLeftColor="blue.400"
                        bg="white"
                    >
                        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color="blue.600" lineHeight="1">
                            {formatCurrency(totalCourseFee)}
                        </Text>
                        <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">
                            Total Course Fee
                        </Text>
                    </Box>
                    <Box
                        p={{ base: 3, md: 4 }}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderLeftWidth="3px"
                        borderLeftColor="green.400"
                        bg="white"
                    >
                        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color="green.600" lineHeight="1">
                            {formatCurrency(amountPaid)}
                        </Text>
                        <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">
                            Amount Paid
                        </Text>
                        {totalInstalments > 0 && (
                            <Text fontSize="2xs" color="gray.500" mt={0.5}>
                                {paidInstalments} of {totalInstalments} installments done
                            </Text>
                        )}
                    </Box>
                    <Box
                        p={{ base: 3, md: 4 }}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderLeftWidth="3px"
                        borderLeftColor={totalBalance > 0 ? "orange.400" : "green.400"}
                        bg="white"
                    >
                        <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontWeight="800"
                            color={totalBalance > 0 ? "orange.600" : "green.600"}
                            lineHeight="1"
                        >
                            {formatCurrency(totalBalance)}
                        </Text>
                        <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">
                            Total Balance
                        </Text>
                        {overdueAmount > 0 && (
                            <Text fontSize="2xs" color="red.500" mt={0.5} fontWeight="600">
                                {formatCurrency(overdueAmount)} overdue
                            </Text>
                        )}
                    </Box>
                </SimpleGrid>
            )}

            {certifications.map((cert) => {
                const certInvoices = (invoices[cert.id] || []).filter((inv) => !isSupersededInvoice(inv));
                if (certInvoices.length === 0) return null;

                return (
                    <Box
                        key={cert.id}
                        p={{ base: 4, md: 5 }}
                        bg="white"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.200"
                    >
                        <HStack spacing={2} mb={{ base: 3, md: 4 }}>
                            <Icon as={FiDollarSign} color="green.500" boxSize={5} flexShrink={0} />
                            <Heading size={{ base: "xs", md: "sm" }} color={COLORS.maastrichtBlue}>
                                {cert.full_certification?.name}
                            </Heading>
                        </HStack>

                        {/* Mobile Card View */}
                        <VStack spacing={2.5} align="stretch" display={{ base: "flex", md: "none" }}>
                            {certInvoices.map((invoice) => {
                                const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                const subtotalAmount = invoice.invoice_line_items?.length > 0
                                    ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
                                const gstAmount = invoice.gst_amount || 0;
                                const totalAmount = subtotalAmount + gstAmount;
                                const hasPdf = hasInvoicePdf(invoice);
                                const dueInBadge = getDueInBadge(invoice);
                                return (
                                    <Box
                                        key={invoice.id}
                                        bg="gray.50"
                                        p={3}
                                        borderRadius="xl"
                                        border="1px solid"
                                        borderColor={isPaid ? "green.200" : "gray.200"}
                                        borderLeft={isPaid ? "3px solid" : "1px solid"}
                                        borderLeftColor={isPaid ? "green.400" : "gray.200"}
                                        transition="all 0.2s"
                                    >
                                        <VStack align="stretch" spacing={2.5}>
                                            <Flex justify="space-between" align="flex-start" gap={3}>
                                                <Box flex="1" minW={0}>
                                                    <Text
                                                      fontWeight="700"
                                                      fontSize="sm"
                                                      color="gray.800"
                                                      noOfLines={1}
                                                      textTransform="capitalize"
                                                    >
                                                      {invoice.invoice_type}
                                                    </Text>
                                                    <HStack spacing={2} mt={1.5} flexWrap="wrap">
                                                        <Badge {...getInvoiceStatusBadgeProps(isPaid)}>
                                                            {isPaid ? "Paid" : invoice.status}
                                                        </Badge>
                                                    </HStack>
                                                </Box>
                                                <Text
                                                  fontWeight="800"
                                                  fontSize="md"
                                                  color={isPaid ? "green.600" : COLORS.maastrichtBlue}
                                                  flexShrink={0}
                                                  whiteSpace="nowrap"
                                                  pl={2}
                                                >
                                                  {formatCurrency(totalAmount)}
                                                </Text>
                                            </Flex>

                                            <VStack align="stretch" spacing={1.5} pt={1}>
                                            <Flex justify="space-between" align="center">
                                              <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due Date</Text>
                                              <Text fontSize="xs" color="gray.700">{formatInvoiceDate(invoice.due_date)}</Text>
                                            </Flex>
                                            <Flex justify="space-between" align="center">
                                                <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due in</Text>
                                                {dueInBadge ? (
                                                    <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2.5} py={0.5} borderRadius="full" textTransform="none">
                                                        {dueInBadge.label}
                                                    </Badge>
                                                ) : (
                                                    <Text fontSize="xs" color="gray.500">—</Text>
                                                )}
                                            </Flex>
                                            {invoice.invoice_line_items?.length > 0 && (
                                                <Box>
                                                  <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5}>Description</Text>
                                                  <Text fontSize="xs" color="gray.700" noOfLines={2}>
                                                    {invoice.invoice_line_items.map(item => item.description).join(", ")}
                                                  </Text>
                                                </Box>
                                            )}
                                            </VStack>
                                            {hasPdf && (
                                                <Flex justify="flex-end" pt={1}>
                                                    <PdfActionIcons
                                                        item={invoice}
                                                        onView={handleViewInvoice}
                                                        viewLabel="View invoice"
                                                        downloadLabel="Download invoice"
                                                        getDownloadFilename={getInvoiceDownloadFilename}
                                                        justify="flex-end"
                                                    />
                                                </Flex>
                                            )}
                                        </VStack>
                                    </Box>
                                );
                            })}
                        </VStack>

                        {/* Desktop Table View */}
                        <Box overflowX="auto" w="100%" display={{ base: "none", md: "block" }} borderRadius="xl" border="1px solid" borderColor="gray.200">
                            <Table variant="simple" size="sm" bg="white">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Due Date</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Due in</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Description</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200" isNumeric>Subtotal</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200" isNumeric>GST</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200" isNumeric>Total</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Status</Th>
                                        <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {certInvoices.map((invoice) => {
                                        const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                        const subtotalAmount = invoice.invoice_line_items?.length > 0
                                            ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
                                        const gstAmount = invoice.gst_amount || 0;
                                        const totalAmount = subtotalAmount + gstAmount;
                                        const hasPdf = hasInvoicePdf(invoice);
                                        const dueInBadge = getDueInBadge(invoice);
                                        return (
                                            <Tr
                                                key={invoice.id}
                                                bg={isPaid ? "green.50" : "white"}
                                                _hover={{ bg: isPaid ? "green.100" : "gray.50" }}
                                                borderLeft={isPaid ? "3px solid" : "none"}
                                                borderColor={isPaid ? "green.400" : "transparent"}
                                                transition="all 0.2s"
                                            >
                                                <Td fontWeight={isPaid ? "semibold" : "normal"}>{formatInvoiceDate(invoice.due_date)}</Td>
                                                <Td>
                                                    {dueInBadge ? (
                                                        <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2.5} py={0.5} borderRadius="full" textTransform="none">
                                                            {dueInBadge.label}
                                                        </Badge>
                                                    ) : (
                                                        <Text as="span" color="gray.400">—</Text>
                                                    )}
                                                </Td>
                                                <Td fontWeight={isPaid ? "semibold" : "normal"}>{invoice.invoice_line_items?.length > 0 ? invoice.invoice_line_items.map(item => item.description).join(", ") : "-"}</Td>
                                                <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>{formatCurrency(subtotalAmount)}</Td>
                                                <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>{formatCurrency(gstAmount)}</Td>
                                                <Td isNumeric fontWeight="bold" color={isPaid ? "green.600" : "gray.800"}>{formatCurrency(totalAmount)}</Td>
                                                <Td>
                                                    <Badge {...getInvoiceStatusBadgeProps(isPaid)}>
                                                        {isPaid ? "Paid" : invoice.status}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <PdfActionIcons
                                                        item={invoice}
                                                        onView={handleViewInvoice}
                                                        viewLabel="View invoice"
                                                        downloadLabel="Download invoice"
                                                        getDownloadFilename={getInvoiceDownloadFilename}
                                                    />
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                );
            })}

            {allInvoices.length === 0 && (
                <Box p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
                    <Text color="gray.500" fontSize="sm">No invoices available.</Text>
                </Box>
            )}
        </VStack>
    );
};
